import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";

const ROWS_OPTIONS = [5, 10, 25];
const DATE_RANGES  = ["Today", "This Week", "This Month", "This Year", "Custom"];

const FILTER_TYPE_MAP = {
  "Today": "today", "This Week": "week", "This Month": "month", "This Year": "year", "Custom": "custom",
};

const STATUS_BADGE = {
  pending: "badge-amber", ongoing: "badge-blue", hold: "badge-gray",
  completed: "badge-green", revisit: "badge-teal", draft: "badge-purple",
};

const getApiUrl = () => {
  const url = process.env.REACT_APP_API_URL || (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL);
  if (url && typeof url === "string" && url.trim() !== "") {
    return url.trim();
  }
  return "https://jobtracking.thesalonx.in/api";
};

const getPhotoBaseUrl = () => {
  const apiUrl = getApiUrl();
  const origin = apiUrl.replace(/\/api\/?$/, "");
  return `${origin}/uploads/job/`;
};

const toPhotoUrl = (filename) => {
  if (!filename) return null;
  let nameStr = filename;
  if (typeof filename === "object" && filename !== null) {
    nameStr = filename.url || filename.filename || filename.photo;
  }
  if (!nameStr || typeof nameStr !== "string") return null;
  const trimmed = nameStr.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("data:") || trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("blob:")) {
    return trimmed;
  }

  const basename = trimmed.split("/").pop();
  return `${getPhotoBaseUrl()}${basename}`;
};
const toPhotoUrlList = (list) => (list || []).map((p) => toPhotoUrl(p.photo || p.filename || p)).filter(Boolean);

/* ─────────────────────────── Formatting helpers ─────────────────────────── */
const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  if (/^\d{2}:\d{2}/.test(iso)) return iso.slice(0, 5);
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
};
const fmtDuration = (mins) => {
  if (mins === null || mins === undefined) return "—";
  const h = Math.floor(mins / 60), m = mins % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};
const fmtHM = (mins) => { const h = Math.floor((mins || 0) / 60), m = (mins || 0) % 60; return `${h}h ${m}m`; };
const fmtJobId = (row) => row?.job_no || `JOB-${String(row?.id).padStart(4, "0")}`;
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const getActivityTagClass = (type) => {
  if (!type) return "act-tag";
  const t = type.toLowerCase();
  if (t.includes("travelling") || t.includes("before photo")) return "act-tag blue";
  if (t.includes("cleaning")) return "act-tag green";
  if (t.includes("briefing")) return "act-tag amber";
  if (t.includes("wrapping")) return "act-tag purple";
  return "act-tag";
};

const getMaterialTagClass = (name) => {
  if (!name) return "mat-tag mat-tag-gray";
  const n = name.toLowerCase();
  if (n.includes("vinyl")) return "mat-tag mat-tag-purple";
  return "mat-tag mat-tag-gray";
};

/* ─── Export helpers ─── */
function downloadCSV(filename, headers, rows) {
  try {
    const blob = new Blob([[headers, ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("CSV export failed:", err);
    alert("Failed to export CSV. Please try again.");
  }
}
function downloadPDF(title, headers, rows) {
  const win = window.open("", "_blank");
  if (!win) {
    alert("Please allow pop-ups to export PDF reports.");
    return;
  }
  win.document.write(`<html><head><title>${title}</title>
    <style>body{font-family:Arial,sans-serif;font-size:12px;padding:20px}h2{margin-bottom:16px;color:#111}
    table{width:100%;border-collapse:collapse}th{background:#5b4cf5;color:#fff;padding:8px 10px;text-align:left;font-size:11px;letter-spacing:.05em}
    td{padding:7px 10px;border-bottom:1px solid #e5e7eb;color:#374151}tr:nth-child(even) td{background:#f9fafb}</style></head>
    <body><h2>${title}</h2><table><thead><tr>${headers.split(",").map((h) => `<th>${h}</th>`).join("")}</tr></thead>
    <tbody>${rows.map((r) => `<tr>${r.split(",").map((c) => `<td>${c}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`);
  win.document.close();
  setTimeout(() => win.print(), 300);
}

/* ─────────────────────────── Styles (unchanged) ─────────────────────────── */
const STYLES = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  *, *::before, *::after { box-sizing: border-box; }
  .rpt-wrap { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #111827; font-size: 14px; max-width: 1500px; margin: 0 auto; }
  .rpt-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; flex-wrap: wrap; gap: 10px; }
  .rpt-title { font-size: 1.15rem; font-weight: 700; color: #1f2937; }
  .rpt-export-row { display: flex; gap: 8px; }
  .rpt-export-btn { display: inline-flex; align-items: center; gap: 6px; font-size: .78rem; font-weight: 600; color: #4b5563; border: 1px solid #e5e7eb; background: #fff; border-radius: 8px; padding: 7px 13px; cursor: pointer; font-family: inherit; }
  .rpt-export-btn:hover { background: #f9fafb; }
  .rpt-tabs { display: flex; gap: 8px; margin-bottom: 14px; flex-wrap: wrap; }
  .rpt-tab { padding: 7px 16px; font-size: .8rem; font-weight: 600; color: #6b7280; border: 1px solid #e5e7eb; border-radius: 8px; cursor: pointer; background: #fff; font-family: inherit; transition: all .12s; }
  .rpt-tab:hover { background: #f3f4f6; }
  .rpt-tab-on { background: #5b4cf5; color: #fff; border-color: #5b4cf5; }
  .rpt-filter-bar { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; transition: opacity .15s; }
  .rpt-filter-loading { opacity: .6; }
  .rpt-pills { display: flex; gap: 6px; flex-wrap: wrap; }
  .rpt-pill { padding: 5px 13px; font-size: .74rem; font-weight: 600; color: #6b7280; border: 1px solid #e5e7eb; border-radius: 7px; cursor: pointer; background: #fff; font-family: inherit; }
  .rpt-pill:hover { background: #f3f4f6; }
  .rpt-pill-on { background: #eef2ff; color: #5b4cf5; border-color: #c7d2fe; }
  .rpt-filter-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .rpt-search-wrap { position: relative; }
  .rpt-search-ico { position: absolute; left: 9px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
  .rpt-search { padding: 6px 10px 6px 28px; font-size: .76rem; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; outline: none; width: 220px; font-family: inherit; }
  .rpt-search:focus { border-color: #818cf8; }
  .rpt-sel { border: 1px solid #e5e7eb; border-radius: 7px; padding: 6px 26px 6px 10px; font-size: .76rem; color: #374151; background: #fff; outline: none; appearance: none; font-family: inherit; cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 8px center; }
  .rpt-sel-sub { margin-bottom: 10px; }
  .rpt-apply-btn { padding: 6px 16px; font-size: .78rem; font-weight: 700; color: #fff; background: #5b4cf5; border: none; border-radius: 8px; cursor: pointer; font-family: inherit; }
  .rpt-apply-btn:hover:not(:disabled) { background: #4338ca; }
  .rpt-apply-btn:disabled { opacity: .6; cursor: not-allowed; }
  .rpt-sub-row { margin-bottom: 8px; }
  .rpt-custom-date { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
  .rpt-dr-lbl { font-size: .74rem; color: #6b7280; font-weight: 600; }
  .rpt-date-inp { border: 1px solid #e5e7eb; border-radius: 7px; padding: 5px 9px; font-size: .76rem; color: #374151; background: #fff; outline: none; font-family: inherit; }
  .rpt-date-inp:focus { border-color: #5b4cf5; }
  .rpt-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 14px; }
  @media (max-width: 800px) { .rpt-summary { grid-template-columns: 1fr; } }
  .rpt-sum-card { border-radius: 10px; padding: 16px 20px; border: 1px solid #e5e7eb; border-top-width: 4px; }
  .rpt-sum-blue { background: #eff6ff; border-top-color: #3b82f6; }
  .rpt-sum-orange { background: #fffbeb; border-top-color: #f59e0b; }
  .rpt-sum-indigo { background: #eef2ff; border-top-color: #5b4cf5; }
  .rpt-sum-val { font-size: 1.6rem; font-weight: 700; color: #1f2937; }
  .rpt-sum-lbl { font-size: .72rem; color: #6b7280; font-weight: 600; margin-top: 2px; }
  .rpt-error-banner { display: flex; align-items: center; gap: 8px; background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; font-size: .8rem; border-radius: 9px; padding: 10px 14px; margin-bottom: 12px; }
  .rpt-retry { margin-left: auto; font-size: .75rem; font-weight: 700; color: #b91c1c; background: none; border: 1px solid #fecaca; border-radius: 6px; padding: 3px 10px; cursor: pointer; }
  .rpt-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; position: relative; margin-bottom: 20px; }
 .rpt-scroll {
  width: 100%;
  overflow-x: hidden;
}
.rpt-tbl {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: .76rem;
}
.rpt-tbl th {
  background: #f9fafb;
  padding: 9px 8px;
  text-align: left;
  font-size: .62rem;
  font-weight: 700;
  color: #9ca3af;
  letter-spacing: .03em;
  text-transform: uppercase;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rpt-tbl td {
  padding: 10px 8px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rpt-tbl tbody tr:last-child td {
  border-bottom: none;
}
.rpt-tbl tbody tr:hover td {
  background: #fafafa;
}
  .rpt-tbl th { background: #f9fafb; padding: 10px 14px; text-align: left; font-size: .68rem; font-weight: 700; color: #9ca3af; letter-spacing: .05em; text-transform: uppercase; border-bottom: 1px solid #e5e7eb; white-space: nowrap; user-select: none; }
  .rpt-tbl th:hover { color: #5b4cf5; }
  .rpt-tbl td { padding: 11px 14px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
  .rpt-tbl tbody tr:last-child td { border-bottom: none; }
  .rpt-tbl tbody tr:hover td { background: #fafafa; }
  .rpt-sort { margin-left: 4px; font-size: .65rem; }
  .rpt-sort-on { color: #5b4cf5; }
  .rpt-sort-off { color: #cbd5e1; }
  .rpt-td-id { font-weight: 700; color: #374151; }
  .rpt-td-bold { font-weight: 600; color: #1f2937; }
  .rpt-td-muted { color: #6b7280; }
  .rpt-badge { display: inline-flex; align-items: center; padding: 3px 11px; border-radius: 20px; font-size: .68rem; font-weight: 700; white-space: nowrap; }
  .badge-completed, .badge-green { background: #d1fae5; color: #065f46; }
  .badge-ongoing, .badge-blue { background: #dbeafe; color: #1d4ed8; }
  .badge-hold, .badge-gray { background: #f3f4f6; color: #374151; }
  .badge-pending, .badge-amber { background: #fef3c7; color: #92400e; }
  .badge-revisit, .badge-teal { background: #ccfbf1; color: #0f766e; }
  .badge-purple { background: #ede9fe; color: #5b21b6; }

  .act-tag { display: inline-flex; align-items: center; padding: 2px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; background: #F3F4F6; color: #374151; white-space: nowrap; }
  .act-tag.blue { background: #DBEAFE; color: #1D4ED8; }
  .act-tag.green { background: #D1FAE5; color: #065F46; }
  .act-tag.amber { background: #FEF3C7; color: #92400E; }
  .act-tag.purple { background: #EDE9FE; color: #5B21B6; }

  .mat-tag { display: inline-flex; align-items: center; padding: 2px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; white-space: nowrap; }
  .mat-tag-purple { background: #EDE9FE; color: #5B21B6; }
  .mat-tag-gray { background: #F3F4F6; color: #374151; }

  .prod-stat { border-radius: 10px; padding: 18px 22px; border: 1px solid #E5E7EB; background: #fff; border-top-width: 4px; }

  .rpt-view-btn { font-size: .75rem; font-weight: 700; color: #5b4cf5; background: none; border: none; cursor: pointer; font-family: inherit; }
  .rpt-view-btn:hover { text-decoration: underline; }

  .rpt-overlay { position: absolute; inset: 0; background: rgba(255,255,255,.6); display: flex; align-items: center; justify-content: center; z-index: 5; }
  .rpt-spinner { width: 30px; height: 30px; border: 3px solid #e5e7eb; border-top-color: #5b4cf5; border-radius: 50%; animation: spin .7s linear infinite; }
  .rpt-skel { height: 12px; border-radius: 4px; background: linear-gradient(90deg,#f3f4f6 25%,#e5e7eb 37%,#f3f4f6 63%); background-size: 400% 100%; animation: shimmer 1.4s ease infinite; }
  @keyframes shimmer { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }

  .rpt-empty-state { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 40px 20px; color: #9ca3af; font-size: .85rem; }

  .rpt-pagination { display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; border-top: 1px solid #f0f2f5; background: #fafafa; flex-wrap: wrap; gap: 10px; }
  .rpt-rpp { display: flex; align-items: center; gap: 6px; font-size: .74rem; color: #6b7280; }
  .rpt-rpp-sel { border: 1px solid #e5e7eb; border-radius: 6px; padding: 3px 22px 3px 7px; font-size: .74rem; background: #fff; outline: none; appearance: none; font-family: inherit;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='9' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position: right 6px center; }
  .rpt-pg-info { font-size: .74rem; color: #9ca3af; }
  .rpt-pg-nav { display: flex; align-items: center; gap: 4px; }
  .rpt-pg-btn { width: 26px; height: 26px; border: 1px solid #e5e7eb; border-radius: 6px; background: #fff; font-size: .72rem; color: #6b7280; cursor: pointer; }
  .rpt-pg-btn:hover:not(:disabled) { background: #f3f4f6; }
  .rpt-pg-btn:disabled { opacity: .4; cursor: not-allowed; }
  .rpt-pg-cur { font-size: .74rem; color: #6b7280; padding: 0 4px; }

  .rpt-desktop { display: block; }
  .rpt-mobile { display: none; }
  @media (max-width: 900px) {
    .rpt-desktop { display: none; }
    .rpt-mobile { display: block; }
  }
  .rpt-mc { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px; margin-bottom: 10px; }
  .rpt-mc-top { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 8px; }
  .rpt-mc-num { font-size: .7rem; font-weight: 700; color: #5b4cf5; }
  .rpt-mc-name { font-size: .88rem; font-weight: 700; color: #1f2937; margin-top: 2px; }
  .rpt-mc-body { display: flex; flex-direction: column; gap: 4px; margin-bottom: 8px; }
  .rpt-mc-row { display: flex; justify-content: space-between; font-size: .78rem; color: #374151; }
  .rpt-mc-key { color: #9ca3af; font-weight: 600; }
  .rpt-mc-foot { border-top: 1px solid #f0f2f5; padding-top: 8px; }
  .rpt-view-full { width: 100%; text-align: center; border: 1px solid #e0e7ff; border-radius: 8px; padding: 7px; background: #eef2ff; }
  .rpt-mob-load, .rpt-mob-empty { text-align: center; padding: 24px; color: #9ca3af; font-size: .82rem; }

  /* ── Job Detail slide-over ── */
  .jdp-overlay { position: fixed; inset: 0; background: rgba(17,24,39,.45); z-index: 200; animation: fadeIn .15s ease; }
  .jdp-panel { position: fixed; top: 0; left: 0; right: 0; bottom: 0; width: 100vw; width: 100%; height: 100vh; height: 100dvh; background: #fff; z-index: 201; box-shadow: none; display: flex; flex-direction: column; animation: slideIn .25s cubic-bezier(.4,0,.2,1); }
  .jdp-head { border-bottom: 1px solid #e5e7eb; padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
  .jdp-head-items { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
  .jdp-hi-lbl { font-size: .62rem; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 3px; }
  .jdp-hi-val { font-size: .82rem; font-weight: 700; color: #111827; }
  .jdp-divider { width: 1px; height: 32px; background: #e5e7eb; }
  .jdp-close { background: #f3f4f6; border: none; border-radius: 8px; width: 30px; height: 30px; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #6b7280; flex-shrink: 0; }
  .jdp-tabs { border-bottom: 1px solid #e5e7eb; padding: 0 20px; display: flex; gap: 0; flex-wrap: wrap; }
  .jdp-tab { padding: 9px 16px; font-size: .78rem; font-weight: 600; color: #6b7280; border: none; background: none; border-bottom: 2px solid transparent; cursor: pointer; font-family: inherit; }
  .jdp-tab:hover { color: #111827; }
  .jdp-tab-on { color: #5b4cf5; border-bottom-color: #5b4cf5; }
  .jdp-body { flex: 1; overflow-y: auto; padding: 18px; background: #f9fafb; }
  .jdp-stat-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 14px; }
  @media (max-width: 640px) { .jdp-stat-row { grid-template-columns: 1fr; } }
  .jdp-stat { border-radius: 10px; padding: 14px 16px; border: 1px solid; border-top-width: 4px; }
  .jdp-stat-blue { background: #eff6ff; border-color: #bfdbfe; border-top-color: #3b82f6; }
  .jdp-stat-green { background: #f0fdf4; border-color: #bbf7d0; border-top-color: #10b981; }
  .jdp-stat-amber { background: #fffbeb; border-color: #fde68a; border-top-color: #f59e0b; }
  .jdp-stat-lbl { font-size: .62rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 5px; }
  .jdp-stat-val { font-size: 1.35rem; font-weight: 700; color: #1f2937; }
  .jdp-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; margin-bottom: 12px; }
  .jdp-card-hd { padding: 11px 15px; border-bottom: 1px solid #e5e7eb; }
  .jdp-card-hd .t { font-size: .82rem; font-weight: 700; color: #111827; }
  .jdp-card-hd .s { font-size: .68rem; color: #9ca3af; margin-top: 1px; }
  .jdp-mini-table { width: 100%; border-collapse: collapse; font-size: .76rem; }
  .jdp-mini-table th { background: #f9fafb; padding: 7px 12px; text-align: left; font-size: .62rem; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e5e7eb; white-space: nowrap; }
  .jdp-mini-table.warn th { background: #fffbeb; color: #92400e; border-bottom-color: #fde68a; }
  .jdp-mini-table td { padding: 8px 12px; border-bottom: 1px solid #f3f4f6; }
  .jdp-mini-table tr:last-child td { border-bottom: none; }
  .jdp-empty-row { text-align: center; color: #9ca3af; padding: 18px; font-size: .78rem; }
  .jdp-article { border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; margin-bottom: 10px; background: #fff; }
  .jdp-article-hd { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: #f9fafb; cursor: pointer; gap: 10px; }
  .jdp-article-num { width: 24px; height: 24px; border-radius: 50%; background: #eef2ff; color: #5b4cf5; display: flex; align-items: center; justify-content: center; font-size: .68rem; font-weight: 700; flex-shrink: 0; }
  .jdp-article-title { font-size: .8rem; font-weight: 600; color: #111827; }
  .jdp-article-sub { font-size: .68rem; color: #9ca3af; margin-left: 6px; }
  .jdp-article-body { padding: 12px 14px; }
  .jdp-thumb { width: 40px; height: 40px; border-radius: 8px; object-fit: cover; border: 1px solid #e5e7eb; cursor: zoom-in; }
  .jdp-thumb-empty { width: 40px; height: 40px; border-radius: 8px; border: 1.5px dashed #d1d5db; background: #f9fafb; }
  .jdp-photo-cell { display: flex; gap: 5px; flex-wrap: wrap; }
  .jdp-loading { display: flex; align-items: center; justify-content: center; height: 100%; color: #9ca3af; font-size: .85rem; gap: 8px; }

  .lightbox-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.88); z-index: 300; display: flex; align-items: center; justify-content: center; }
  .lightbox-close { position: absolute; top: 20px; right: 26px; color: #fff; font-size: 26px; cursor: pointer; z-index: 301; background: none; border: none; }
  .lightbox-img { max-width: 90vw; max-height: 90vh; border-radius: 10px; }

.rpt-col-id { width: 8%; }
.rpt-col-client_name { width: 11%; }
.rpt-col-location { width: 10%; }
.rpt-col-work_type { width: 11%; }
.rpt-col-staff_name { width: 12%; }
.rpt-col-created_at { width: 9%; }
.rpt-col-updated_at { width: 10%; }
.rpt-col-activity_duration { width: 10%; }
.rpt-col-status { width: 10%; }
.rpt-col-action { width: 9%; }

.jdp-aa-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; margin-bottom: 18px; overflow: hidden; }
.jdp-aa-head { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; background: #fff; border-bottom: 1px solid #e5e7eb; }
.jdp-aa-head-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.jdp-aa-number { width: 30px; height: 30px; min-width: 30px; border-radius: 50%; background: #5b4cf5; color: #fff; font-size: .75rem; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.jdp-aa-title { color: #111827; font-size: .88rem; font-weight: 700; margin-bottom: 4px; }
.jdp-aa-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 5px; color: #6b7280; font-size: .7rem; }
.jdp-aa-dot { color: #c4c7ce; padding: 0 2px; }
.jdp-aa-head-right { display: flex; align-items: center; justify-content: flex-end; max-width: 45%; }
.jdp-aa-material-summary { font-size: .68rem; color: #6b7280; line-height: 1.5; text-align: right; }
.jdp-aa-section { padding: 15px 16px; border-bottom: 1px solid #eef0f3; }
.jdp-aa-section:last-child { border-bottom: none; }
.jdp-aa-section-title { font-size: .72rem; font-weight: 700; color: #374151; margin-bottom: 10px; }
.jdp-aa-table-wrap { width: 100%; overflow-x: auto; border: 1px solid #e5e7eb; border-radius: 8px; }
.jdp-aa-table { width: 100%; min-width: 850px; border-collapse: collapse; background: #fff; }
.jdp-aa-table th { background: #f8f9fb; color: #6b7280; font-size: .59rem; font-weight: 700; text-align: left; padding: 9px 10px; border-bottom: 1px solid #e5e7eb; white-space: nowrap; letter-spacing: .025em; }
.jdp-aa-table td { padding: 10px; color: #4b5563; font-size: .7rem; border-bottom: 1px solid #f0f1f3; vertical-align: top; line-height: 1.45; }
.jdp-aa-table tbody tr:last-child td { border-bottom: none; }
.jdp-aa-strong { color: #111827 !important; font-weight: 700; }
.jdp-aa-description { min-width: 180px; max-width: 260px; }
.jdp-aa-type { display: inline-flex; align-items: center; padding: 4px 8px; border-radius: 5px; background: #f3f0ff; color: #5b4cf5; font-size: .64rem; font-weight: 600; white-space: nowrap; }
.jdp-aa-materials { display: flex; flex-wrap: wrap; gap: 5px; min-width: 130px; }
.jdp-aa-material { display: inline-flex; align-items: center; background: #eef2ff; color: #4f46e5; border-radius: 5px; padding: 3px 6px; font-size: .61rem; font-weight: 600; white-space: nowrap; }
.jdp-aa-activity-title { display: flex; align-items: center; gap: 5px; color: #111827; font-size: .74rem; font-weight: 700; margin-bottom: 10px; }
.jdp-aa-activity-title-light { color: #6b7280; font-weight: 500; }
.jdp-aa-activity-table { min-width: 800px; }
.jdp-aa-sno { width: 50px; color: #6b7280 !important; font-weight: 600; }
.jdp-aa-activity-name { color: #111827 !important; font-weight: 600; min-width: 170px; }
.jdp-aa-empty { padding: 25px !important; text-align: center; color: #9ca3af !important; font-size: .72rem !important; }
.jdp-aa-table tbody tr { transition: background .15s ease; }
.jdp-aa-table tbody tr:hover { background: #fafafa; }

@media (max-width: 900px) {
  .jdp-aa-head { align-items: flex-start; }
  .jdp-aa-head-right { max-width: 35%; }
}
@media (max-width: 640px) {
  .jdp-aa-card { border-radius: 10px; margin-bottom: 14px; }
  .jdp-aa-head { padding: 12px; align-items: flex-start; flex-direction: column; gap: 8px; }
  .jdp-aa-head-right { max-width: 100%; width: 100%; justify-content: flex-start; }
  .jdp-aa-material-summary { text-align: left; }
  .jdp-aa-section { padding: 12px; }
  .jdp-aa-number { width: 27px; height: 27px; min-width: 27px; }
  .jdp-aa-title { font-size: .82rem; }
}
`;

/* ─── Small shared pieces ─── */
const SortIcon = ({ col, sortKey, sortDir }) => (
  <span className={`rpt-sort ${sortKey === col ? "rpt-sort-on" : "rpt-sort-off"}`}>
    {sortKey === col ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}
  </span>
);

const SkeletonRows = ({ cols, count }) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={i}>{cols.map((c) => (
      <td key={c.key} style={{ padding: "12px 14px" }}>
        <div className="rpt-skel" style={{ width: c.key === "action" ? 50 : "80%" }} />
      </td>
    ))}</tr>
  ));

function PaginationBar({ meta, page, setPage, rowsPerPage, setRowsPerPage, loading }) {
  const totalPages = meta?.last_page ?? 1;
  const totalRecords = meta?.total ?? 0;
  const from = meta?.from ?? 0, to = meta?.to ?? 0;
  return (
    <div className="rpt-pagination">
      <div className="rpt-rpp">
        <span>Rows per page:</span>
        <select className="rpt-rpp-sel" value={rowsPerPage}
          onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
          {ROWS_OPTIONS.map((n) => <option key={n}>{n}</option>)}
        </select>
      </div>
      <span className="rpt-pg-info">{totalRecords === 0 ? "0 results" : `${from}–${to} of ${totalRecords}`}</span>
      <div className="rpt-pg-nav">
        <button className="rpt-pg-btn" onClick={() => setPage(1)} disabled={page === 1 || loading}>«</button>
        <button className="rpt-pg-btn" onClick={() => setPage((p) => p - 1)} disabled={page === 1 || loading}>‹</button>
        <span className="rpt-pg-cur">{page} / {totalPages}</span>
        <button className="rpt-pg-btn" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages || loading}>›</button>
        <button className="rpt-pg-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages || loading}>»</button>
      </div>
    </div>
  );
}

const ErrorBanner = ({ error, onRetry }) => !error ? null : (
  <div className="rpt-error-banner">
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    {error}
    <button className="rpt-retry" onClick={onRetry}>Retry</button>
  </div>
);

const EmptyState = ({ message, onClear }) => (
  <div className="rpt-empty-state">
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.2">
      <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 13h4" strokeLinecap="round" />
    </svg>
    <span>{message}</span>
    {onClear && <button className="rpt-apply-btn" style={{ marginTop: 6 }} onClick={onClear}>Clear Filters</button>}
  </div>
);

function MainFilterBar({ loading, dateRange, setDateRange, search, setSearch,
  statusFilter, setStatusFilter, staffFilter, setStaffFilter, staffOptions, onApply, onReset }) {
  return (
    <div className={`rpt-filter-bar${loading ? " rpt-filter-loading" : ""}`}>
      <div className="rpt-pills">
        {DATE_RANGES.map((r) => (
          <button key={r} className={`rpt-pill${dateRange === r ? " rpt-pill-on" : ""}`}
            onClick={() => setDateRange(r === dateRange ? null : r)}>{r}</button>
        ))}
      </div>
      <div className="rpt-filter-right">
        <div className="rpt-search-wrap">
          <svg className="rpt-search-ico" width="13" height="13" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
            <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <input className="rpt-search" type="text" placeholder="Search by job ID, client, staff…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onApply()} />
        </div>
        <select className="rpt-sel" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">Any Status</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending</option>
          <option value="ongoing">Ongoing</option>
          <option value="hold">Hold</option>
          <option value="completed">Completed</option>
          <option value="revisit">Revisit</option>
        </select>
        <select className="rpt-sel" value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)}>
          <option value="">All Staff</option>
          {staffOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {onReset && (
          <button
            style={{
              padding: "6px 12px", fontSize: ".76rem", fontWeight: 600, color: "#6b7280",
              background: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", cursor: "pointer"
            }}
            onClick={onReset}
          >
            Reset
          </button>
        )}
        <button className="rpt-apply-btn" onClick={onApply} disabled={loading}>
          {loading ? "Loading…" : "Apply Filters"}
        </button>
      </div>
    </div>
  );
}

const CustomDateBar = ({ fromDate, setFromDate, toDate, setToDate, onApply, loading }) => (
  <div className="rpt-custom-date">
    <label className="rpt-dr-lbl">From</label>
    <input type="date" className="rpt-date-inp" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
    <label className="rpt-dr-lbl">To</label>
    <input type="date" className="rpt-date-inp" value={toDate} onChange={(e) => setToDate(e.target.value)} />
    <button className="rpt-apply-btn" onClick={onApply} disabled={loading || !fromDate || !toDate}>Apply</button>
  </div>
);

/* ═══════════════════════════════════════════════════
   Shared server-report hook — one fetch/pagination/filter
   engine reused by all 3 tabs so behavior stays identical.
═══════════════════════════════════════════════════ */
function useServerReport(endpoint, subFilterKey /* e.g. "activity_type" | "material_name" | null */) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [staffFilter, setStaffFilter] = useState("");
  const [subFilter, setSubFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const [applied, setApplied] = useState({
    search: "", statusFilter: "all", staffFilter: "", subFilter: "all",
    dateRange: null, fromDate: "", toDate: "",
  });

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, from: 0, to: 0, last_page: 1 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const buildParams = useCallback(() => {
    const params = {
      per_page: rowsPerPage,
      page,
    };
    if (applied.search) params.search = applied.search;
    if (applied.statusFilter && applied.statusFilter !== "all") params.status = applied.statusFilter;
    if (applied.staffFilter) params.staff_id = applied.staffFilter;
    if (subFilterKey && applied.subFilter && applied.subFilter !== "all") params[subFilterKey] = applied.subFilter;
    if (applied.dateRange) {
      params.date_filter = FILTER_TYPE_MAP[applied.dateRange];
      if (applied.dateRange === "Custom") {
        params.from_date = applied.fromDate;
        params.to_date = applied.toDate;
      }
    }
    return params;
  }, [applied, page, rowsPerPage, subFilterKey]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = buildParams();
      const res = await API.get(endpoint, { params });
      const body = res.data;
      if (!body.success) {
        setError(body.message || "Unable to load report.");
        setRows([]);
        setMeta({ total: 0, from: 0, to: 0, last_page: 1 });
        return;
      }
      const paginator = body.data;
      setRows(paginator.data || []);
      setMeta({
        total: paginator.total ?? 0,
        from: paginator.from ?? 0,
        to: paginator.to ?? 0,
        last_page: paginator.last_page ?? 1,
      });
      if (body.summary) setSummary(body.summary);
    } catch (err) {
      console.error(`${endpoint} error:`, err);
      setError("Unable to load report. Please try again.");
      setRows([]);
      setMeta({ total: 0, from: 0, to: 0, last_page: 1 });
    } finally {
      setLoading(false);
    }
  }, [endpoint, buildParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const av = String(a[sortKey] ?? "").toLowerCase(), bv = String(b[sortKey] ?? "").toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [rows, sortKey, sortDir]);

  const apply = () => {
    setApplied({ search, statusFilter, staffFilter, subFilter, dateRange, fromDate, toDate });
    setPage(1);
  };

  const clear = () => {
    setSearch(""); setStatusFilter("all"); setStaffFilter(""); setSubFilter("all");
    setDateRange(null); setFromDate(""); setToDate("");
    setApplied({ search: "", statusFilter: "all", staffFilter: "", subFilter: "all", dateRange: null, fromDate: "", toDate: "" });
    setPage(1);
  };

  const sort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };

  // Fetch the FULL filtered set (not just current page) for export.
  const fetchAllForExport = useCallback(async () => {
    const params = buildParams();
    params.per_page = 5000;
    params.page = 1;
    const res = await API.get(endpoint, { params });
    return res.data?.success ? (res.data.data?.data || []) : [];
  }, [endpoint, buildParams]);

  return {
    search, setSearch, statusFilter, setStatusFilter, staffFilter, setStaffFilter,
    subFilter, setSubFilter, dateRange, setDateRange, fromDate, setFromDate, toDate, setToDate,
    page, setPage, rowsPerPage, setRowsPerPage, sortKey, sortDir, sort,
    rows: sorted, meta, summary, loading, error, apply, clear, fetchAllForExport, refetch: fetchData,
  };
}

/* ═══════════════════════════════════════════════════
   ROOT CONTENT COMPONENT
═══════════════════════════════════════════════════ */
function JobReportsContentNew() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("job");
  const [exportHandlers, setExportHandlers] = useState({ csv: null, pdf: null });
  const [detailJobId, setDetailJobId] = useState(null);
  const [staffOptions, setStaffOptions] = useState([]);

  useEffect(() => {
    API.get("/reports/filter-options")
      .then((res) => {
        if (res.data?.success) setStaffOptions(res.data.data.staff || []);
      })
      .catch((err) => console.error("filter-options error:", err));
  }, []);

  const openDetail = (id) => {
    if (id) navigate(`/jobs/${id}`);
  };
  const closeDetail = () => setDetailJobId(null);

  return (
    <div className="rpt-wrap">
      <style>{STYLES}</style>

      <div className="rpt-header">
        <h2 className="rpt-title">Reports</h2>
        <div className="rpt-export-row">
          <button className="rpt-export-btn" onClick={() => exportHandlers.csv?.()}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M8 2v8M5 7l3 3 3-3M2 11v1a2 2 0 002 2h8a2 2 0 002-2v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Export CSV
          </button>
          <button className="rpt-export-btn" onClick={() => exportHandlers.pdf?.()}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
              <path d="M5 6h6M5 9h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            Export PDF
          </button>
        </div>
      </div>

      <div className="rpt-tabs">
        {[
          { key: "job", label: "Job Report" },
          { key: "activity", label: "Productivity Report" },
          { key: "material", label: "Material Used Report" },
        ].map(({ key, label }) => (
          <button key={key} className={`rpt-tab${activeTab === key ? " rpt-tab-on" : ""}`} onClick={() => setActiveTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === "job" && <JobReportTab onView={openDetail} reg={setExportHandlers} staffOptions={staffOptions} />}
      {activeTab === "activity" && <ActivityReportTab onView={openDetail} reg={setExportHandlers} staffOptions={staffOptions} />}
      {activeTab === "material" && <MaterialReportTab onView={openDetail} reg={setExportHandlers} staffOptions={staffOptions} />}

      {detailJobId && <JobDetailPanel jobId={detailJobId} onClose={closeDetail} />}
    </div>
  );
}

export default JobReportsContentNew;

/* ═══════════════════════════════════════════════════
   TAB 1 — JOB REPORT
═══════════════════════════════════════════════════ */
function JobReportTab({ onView, reg, staffOptions }) {
  const r = useServerReport("/reports/jobs", null);

  useEffect(() => {
    reg({
      csv: async () => {
        const all = await r.fetchAllForExport();
        const hdr = "Job ID,Client,Location,Work Type,Staff,Start,Last Updated,Status";
        const csv = all.map((j) => [
          fmtJobId(j), j.client_name, j.location, j.work_type,
          (j.staff?.length ? j.staff.map((s) => s.name).join(" | ") : ""),
          fmt(j.created_at), fmt(j.updated_at), j.status,
        ].join(","));
        downloadCSV("job-report.csv", hdr, csv);
      },
      pdf: async () => {
        const all = await r.fetchAllForExport();
        const hdr = "Job ID,Client,Location,Work Type,Staff,Start,Last Updated,Status";
        const csv = all.map((j) => [
          fmtJobId(j), j.client_name, j.location, j.work_type,
          (j.staff?.length ? j.staff.map((s) => s.name).join(" | ") : ""),
          fmt(j.created_at), fmt(j.updated_at), j.status,
        ].join(","));
        downloadPDF("Job Report", hdr, csv);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r.rows]);

  const cols = [
    { key: "id", label: "JOB ID" }, { key: "client_name", label: "CLIENT" }, { key: "location", label: "LOCATION" },
    { key: "work_type", label: "WORK TYPE" }, { key: "staff_name", label: "STAFF" }, { key: "created_at", label: "START" },
    { key: "updated_at", label: "LAST UPDATED" }, { key: "activity_duration", label: "DURATION" },
    { key: "status", label: "STATUS" }, { key: "action", label: "ACTION" },
  ];

  return (
    <>
      <ErrorBanner error={r.error} onRetry={r.refetch} />
      <MainFilterBar loading={r.loading} dateRange={r.dateRange} setDateRange={r.setDateRange}
        search={r.search} setSearch={r.setSearch} statusFilter={r.statusFilter} setStatusFilter={r.setStatusFilter}
        staffFilter={r.staffFilter} setStaffFilter={r.setStaffFilter} staffOptions={staffOptions} onApply={r.apply} onReset={r.clear} />
      {r.dateRange === "Custom" && <CustomDateBar fromDate={r.fromDate} setFromDate={r.setFromDate} toDate={r.toDate} setToDate={r.setToDate} onApply={r.apply} loading={r.loading} />}

      <div className="rpt-card rpt-desktop">
        {r.loading && <div className="rpt-overlay"><div className="rpt-spinner" /></div>}
        <div className="rpt-scroll">
          <table className="rpt-tbl">
            <thead><tr>{cols.map(({ key, label }) => (
              <th key={key} className={`rpt-col-${key}`} onClick={() => key !== "action" && r.sort(key)}
                style={{ cursor: key !== "action" ? "pointer" : "default" }}>
                {label}{key !== "action" && <SortIcon col={key} sortKey={r.sortKey} sortDir={r.sortDir} />}
              </th>
            ))}</tr></thead>
            <tbody>
              {r.loading ? <SkeletonRows cols={cols} count={r.rowsPerPage} /> :
               r.rows.length === 0 ? <tr><td colSpan={cols.length}><EmptyState message="No jobs match your filters." onClear={r.clear} /></td></tr> :
               r.rows.map((j) => (
                <tr key={j.id}>
                  <td className="rpt-td-id rpt-col-id">{fmtJobId(j)}</td>
                  <td className="rpt-td-bold rpt-col-client_name">{j.client_name}</td>
                  <td className="rpt-td-muted rpt-col-location">{j.location}</td>
                  <td className="rpt-col-work_type">{j.work_type}</td>
                  <td className="rpt-td-muted rpt-col-staff_name">{j.staff?.length ? j.staff.map((s) => s.name).join(", ") : "—"}</td>
                  <td className="rpt-td-muted rpt-col-created_at">{fmt(j.created_at)}</td>
                  <td className="rpt-td-muted rpt-col-updated_at">{fmt(j.updated_at)}</td>
                  <td className="rpt-td-muted rpt-col-activity_duration">{j.activity_duration || "00:00:00"}</td>
                  <td className="rpt-col-status"><span className={`rpt-badge ${STATUS_BADGE[j.status] ?? "badge-gray"}`}>{capitalize(j.status)}</span></td>
                  <td className="rpt-col-action"><button className="rpt-view-btn" onClick={() => onView(j.id)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar meta={r.meta} page={r.page} setPage={r.setPage} rowsPerPage={r.rowsPerPage} setRowsPerPage={r.setRowsPerPage} loading={r.loading} />
      </div>

      <div className="rpt-mobile">
        {r.loading ? <div className="rpt-mob-load"><div className="rpt-spinner" /></div> :
         r.rows.length === 0 ? <div className="rpt-mob-empty">No jobs match your filters.</div> :
         r.rows.map((j) => (
          <div key={j.id} className="rpt-mc">
            <div className="rpt-mc-top">
              <div><span className="rpt-mc-num">{fmtJobId(j)}</span><p className="rpt-mc-name">{j.client_name}</p></div>
              <span className={`rpt-badge ${STATUS_BADGE[j.status] ?? "badge-gray"}`}>{capitalize(j.status)}</span>
            </div>
            <div className="rpt-mc-body">
              {[["Location", j.location], ["Work Type", j.work_type],
                ["Staff", j.staff?.length ? j.staff.map((s) => s.name).join(", ") : "—"],
                ["Start", fmt(j.created_at)], ["Last Updated", fmt(j.updated_at)],
                ["Duration", j.activity_duration || "00:00:00"]].map(([k, v]) => (
                <div key={k} className="rpt-mc-row"><span className="rpt-mc-key">{k}</span><span>{v}</span></div>
              ))}
            </div>
            <div className="rpt-mc-foot">
              <button className="rpt-view-btn rpt-view-full" onClick={() => onView(j.id)}>View Details</button>
            </div>
          </div>
        ))}
        {(r.meta?.total ?? 0) > 0 && <PaginationBar meta={r.meta} page={r.page} setPage={r.setPage} rowsPerPage={r.rowsPerPage} setRowsPerPage={r.setRowsPerPage} loading={r.loading} />}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════
   TAB 2 — PRODUCTIVITY / ACTIVITY REPORT
═══════════════════════════════════════════════════ */
function ActivityReportTab({ onView, reg, staffOptions }) {
  const r = useServerReport("/reports/activities", "activity_type");

  useEffect(() => {
    reg({
      csv: async () => {
        const all = await r.fetchAllForExport();
        const hdr = "Date,Staff,Job ID,Client,Activity,Start,End,Duration";
        const csv = all.map((a) => [
          fmt(a.created_at), a.staff_name, a.job_no || fmtJobId({ id: a.job_id }), a.client_name,
          a.activity_type, fmtTime(a.start_time), fmtTime(a.end_time), fmtDuration(a.duration_minutes),
        ].join(","));
        downloadCSV("activity-report.csv", hdr, csv);
      },
      pdf: async () => {
        const all = await r.fetchAllForExport();
        const hdr = "Date,Staff,Job ID,Client,Activity,Start,End,Duration";
        const csv = all.map((a) => [
          fmt(a.created_at), a.staff_name, a.job_no || fmtJobId({ id: a.job_id }), a.client_name,
          a.activity_type, fmtTime(a.start_time), fmtTime(a.end_time), fmtDuration(a.duration_minutes),
        ].join(","));
        downloadPDF("Productivity Report", hdr, csv);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r.rows]);

  const cols = [
    { key: "created_at", label: "DATE" }, { key: "staff_name", label: "STAFF" }, { key: "job_id", label: "JOB ID" },
    { key: "client_name", label: "CLIENT" }, { key: "activity_type", label: "ACTIVITY" }, { key: "start_time", label: "START" },
    { key: "end_time", label: "END" }, { key: "duration_minutes", label: "DURATION" }, { key: "action", label: "ACTION" },
  ];

  const summary = r.summary || { active_minutes: 0, hold_minutes: 0, total_count: 0 };

  return (
    <>
      <ErrorBanner error={r.error} onRetry={r.refetch} />
      <MainFilterBar loading={r.loading} dateRange={r.dateRange} setDateRange={r.setDateRange}
        search={r.search} setSearch={r.setSearch} statusFilter={r.statusFilter} setStatusFilter={r.setStatusFilter}
        staffFilter={r.staffFilter} setStaffFilter={r.setStaffFilter} staffOptions={staffOptions} onApply={r.apply} onReset={r.clear} />

      <div className="rpt-sub-row">
        <select className="rpt-sel rpt-sel-sub" value={r.subFilter} onChange={(e) => r.setSubFilter(e.target.value)}>
          <option value="all">All Activity Types</option>
          <option value="Office Briefing">Office Briefing</option>
          <option value="Travelling">Travelling</option>
          <option value="Transporting">Transporting</option>
          <option value="Material Collection / Return">Material Collection / Return</option>
          <option value="Site Cleaning">Site Cleaning</option>
          <option value="Article Cleaning">Article Cleaning</option>
          <option value="Vinyl Wrapping">Vinyl Wrapping</option>
          <option value="Plumbing">Plumbing</option>
          <option value="Before Photos">Before Photos</option>
          <option value="Hold">Hold</option>
        </select>
      </div>

      {r.dateRange === "Custom" && <CustomDateBar fromDate={r.fromDate} setFromDate={r.setFromDate} toDate={r.toDate} setToDate={r.setToDate} onApply={r.apply} loading={r.loading} />}

      <div className="rpt-summary" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "16px" }}>
        <div className="prod-stat" style={{ borderTopColor: "#3B82F6", background: "#EFF6FF" }}>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "#60A5FA", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: "6px" }}>Total Active Hours</div>
          <div style={{ fontSize: "1.875rem", fontWeight: 700, color: "#1D4ED8", marginBottom: "2px" }}>{fmtHM(summary.active_minutes)}</div>
          <div style={{ fontSize: "11px", color: "#60A5FA", marginTop: "2px" }}>Sum of all activity durations</div>
        </div>
        <div className="prod-stat" style={{ borderTopColor: "#F59E0B", background: "#FFFBEB" }}>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "#F59E0B", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: "6px" }}>Total Hold Hours</div>
          <div style={{ fontSize: "1.875rem", fontWeight: 700, color: "#B45309", marginBottom: "2px" }}>{fmtHM(summary.hold_minutes)}</div>
          <div style={{ fontSize: "11px", color: "#F59E0B", marginTop: "2px" }}>Time on hold status</div>
        </div>
        <div className="prod-stat" style={{ borderTopColor: "#10B981", background: "#F0FDF4" }}>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "#10B981", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: "6px" }}>Activities Logged</div>
          <div style={{ fontSize: "1.875rem", fontWeight: 700, color: "#047857", marginBottom: "2px" }}>{summary.total_count}</div>
          <div style={{ fontSize: "11px", color: "#10B981", marginTop: "2px" }}>Across all filtered staff</div>
        </div>
      </div>

      <div className="rpt-card rpt-desktop">
        {r.loading && <div className="rpt-overlay"><div className="rpt-spinner" /></div>}
        <div className="rpt-scroll">
          <table className="rpt-tbl">
            <thead><tr>{cols.map(({ key, label }) => (
              <th key={key} onClick={() => key !== "action" && r.sort(key)} style={{ cursor: key !== "action" ? "pointer" : "default" }}>
                {label}{key !== "action" && <SortIcon col={key} sortKey={r.sortKey} sortDir={r.sortDir} />}
              </th>
            ))}</tr></thead>
            <tbody>
              {r.loading ? <SkeletonRows cols={cols} count={r.rowsPerPage} /> :
               r.rows.length === 0 ? <tr><td colSpan={cols.length}><EmptyState message="No activities match your filters." /></td></tr> :
               r.rows.map((a) => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600, color: "#111827" }}>{fmt(a.created_at)}</td>
                  <td>{a.staff_name ?? "—"}</td>
                  <td style={{ fontWeight: 700, color: "#5b4cf5", cursor: "pointer" }} onClick={() => onView(a.job_id)}>{a.job_no || fmtJobId({ id: a.job_id })}</td>
                  <td className="rpt-td-bold">{a.client_name}</td>
                  <td><span className={getActivityTagClass(a.activity_type)}>{a.activity_type}</span></td>
                  <td className="rpt-td-muted">{fmtTime(a.start_time)}</td>
                  <td className="rpt-td-muted">{fmtTime(a.end_time)}</td>
                  <td>{fmtDuration(a.duration_minutes)}</td>
                  <td><button className="rpt-view-btn" onClick={() => onView(a.job_id)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar meta={r.meta} page={r.page} setPage={r.setPage} rowsPerPage={r.rowsPerPage} setRowsPerPage={r.setRowsPerPage} loading={r.loading} />
      </div>

      <div className="rpt-mobile">
        {r.loading ? <div className="rpt-mob-load"><div className="rpt-spinner" /></div> :
         r.rows.length === 0 ? <div className="rpt-mob-empty">No activities match your filters.</div> :
         r.rows.map((a) => (
          <div key={a.id} className="rpt-mc">
            <div className="rpt-mc-top">
              <div><span className="rpt-mc-num">{a.job_no || fmtJobId({ id: a.job_id })}</span><p className="rpt-mc-name">{a.client_name}</p></div>
              <span className="rpt-badge badge-purple">{a.activity_type}</span>
            </div>
            <div className="rpt-mc-body">
              {[["Date", fmt(a.created_at)], ["Staff", a.staff_name ?? "—"], ["Start", fmtTime(a.start_time)],
                ["End", fmtTime(a.end_time)], ["Duration", fmtDuration(a.duration_minutes)]].map(([k, v]) => (
                <div key={k} className="rpt-mc-row"><span className="rpt-mc-key">{k}</span><span>{v}</span></div>
              ))}
            </div>
            <div className="rpt-mc-foot">
              <button className="rpt-view-btn rpt-view-full" onClick={() => onView(a.job_id)}>View Details</button>
            </div>
          </div>
        ))}
        {(r.meta?.total ?? 0) > 0 && <PaginationBar meta={r.meta} page={r.page} setPage={r.setPage} rowsPerPage={r.rowsPerPage} setRowsPerPage={r.setRowsPerPage} loading={r.loading} />}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════
   TAB 3 — MATERIAL USED REPORT
═══════════════════════════════════════════════════ */
function MaterialReportTab({ onView, reg, staffOptions }) {
  const r = useServerReport("/reports/materials", "material_name");

  useEffect(() => {
    reg({
      csv: async () => {
        const all = await r.fetchAllForExport();
        const hdr = "Date,Job ID,Client,Staff,Material,Quantity";
        const csv = all.map((m) => [
          fmt(m.created_at), m.job_no || fmtJobId({ id: m.job_id }), m.client_name, m.staff_name, m.material_name, m.quantity,
        ].join(","));
        downloadCSV("material-report.csv", hdr, csv);
      },
      pdf: async () => {
        const all = await r.fetchAllForExport();
        const hdr = "Date,Job ID,Client,Staff,Material,Quantity";
        const csv = all.map((m) => [
          fmt(m.created_at), m.job_no || fmtJobId({ id: m.job_id }), m.client_name, m.staff_name, m.material_name, m.quantity,
        ].join(","));
        downloadPDF("Material Used Report", hdr, csv);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r.rows]);

  const cols = [
    { key: "created_at", label: "DATE" }, { key: "job_id", label: "JOB ID" }, { key: "client_name", label: "CLIENT" },
    { key: "staff_name", label: "STAFF" }, { key: "material_name", label: "MATERIAL" }, { key: "quantity", label: "QUANTITY" },
    { key: "action", label: "ACTION" },
  ];

  return (
    <>
      <ErrorBanner error={r.error} onRetry={r.refetch} />
      <MainFilterBar loading={r.loading} dateRange={r.dateRange} setDateRange={r.setDateRange}
        search={r.search} setSearch={r.setSearch} statusFilter={r.statusFilter} setStatusFilter={r.setStatusFilter}
        staffFilter={r.staffFilter} setStaffFilter={r.setStaffFilter} staffOptions={staffOptions} onApply={r.apply} onReset={r.clear} />

      <div className="rpt-sub-row">
        <select className="rpt-sel rpt-sel-sub" value={r.subFilter} onChange={(e) => r.setSubFilter(e.target.value)}>
          <option value="all">All Materials</option>
          <option value="Vinyl Wrap Rolls">Vinyl Wrap Rolls</option>
          <option value="Carpet Roll">Carpet Roll</option>
          <option value="Stone Veneer">Stone Veneer</option>
          <option value="Epoxy Resin">Epoxy Resin</option>
          <option value="ACP Sheet">ACP Sheet</option>
        </select>
      </div>

      {r.dateRange === "Custom" && <CustomDateBar fromDate={r.fromDate} setFromDate={r.setFromDate} toDate={r.toDate} setToDate={r.setToDate} onApply={r.apply} loading={r.loading} />}

      <div className="rpt-card rpt-desktop">
        {r.loading && <div className="rpt-overlay"><div className="rpt-spinner" /></div>}
        <div className="rpt-scroll">
          <table className="rpt-tbl">
            <thead><tr>{cols.map(({ key, label }) => (
              <th key={key} onClick={() => key !== "action" && r.sort(key)} style={{ cursor: key !== "action" ? "pointer" : "default" }}>
                {label}{key !== "action" && <SortIcon col={key} sortKey={r.sortKey} sortDir={r.sortDir} />}
              </th>
            ))}</tr></thead>
            <tbody>
              {r.loading ? <SkeletonRows cols={cols} count={r.rowsPerPage} /> :
               r.rows.length === 0 ? <tr><td colSpan={cols.length}><EmptyState message="No materials match your filters." /></td></tr> :
               r.rows.map((m) => (
                <tr key={m.id}>
                  <td style={{ fontWeight: 600, color: "#111827" }}>{fmt(m.created_at)}</td>
                  <td style={{ fontWeight: 700, color: "#5b4cf5", cursor: "pointer" }} onClick={() => onView(m.job_id)}>{m.job_no || fmtJobId({ id: m.job_id })}</td>
                  <td className="rpt-td-bold">{m.client_name}</td>
                  <td className="rpt-td-muted">{m.staff_name ?? "—"}</td>
                  <td><span className={getMaterialTagClass(m.material_name)}>{m.material_name}</span></td>
                  <td>{m.quantity} {m.unit || ""}</td>
                  <td><button className="rpt-view-btn" onClick={() => onView(m.job_id)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar meta={r.meta} page={r.page} setPage={r.setPage} rowsPerPage={r.rowsPerPage} setRowsPerPage={r.setRowsPerPage} loading={r.loading} />
      </div>

      <div className="rpt-mobile">
        {r.loading ? <div className="rpt-mob-load"><div className="rpt-spinner" /></div> :
         r.rows.length === 0 ? <div className="rpt-mob-empty">No materials match your filters.</div> :
         r.rows.map((m) => (
          <div key={m.id} className="rpt-mc">
            <div className="rpt-mc-top">
              <div><span className="rpt-mc-num">{m.job_no || fmtJobId({ id: m.job_id })}</span><p className="rpt-mc-name">{m.client_name}</p></div>
              <span className="rpt-badge badge-purple">{m.material_name}</span>
            </div>
            <div className="rpt-mc-body">
              {[["Date", fmt(m.created_at)], ["Staff", m.staff_name ?? "—"], ["Quantity", `${m.quantity} ${m.unit || ""}`]].map(([k, v]) => (
                <div key={k} className="rpt-mc-row"><span className="rpt-mc-key">{k}</span><span>{v}</span></div>
              ))}
            </div>
            <div className="rpt-mc-foot">
              <button className="rpt-view-btn rpt-view-full" onClick={() => onView(m.job_id)}>View Details</button>
            </div>
          </div>
        ))}
        {(r.meta?.total ?? 0) > 0 && <PaginationBar meta={r.meta} page={r.page} setPage={r.setPage} rowsPerPage={r.rowsPerPage} setRowsPerPage={r.setRowsPerPage} loading={r.loading} />}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════
   JOB DETAIL SLIDE-OVER — now built from /reports/jobs/{id}
═══════════════════════════════════════════════════ */
function normalizeReportJobDetail(raw) {
  const jobRaw = raw.job || {};
  const articlesById = {};
  (raw.articles || []).forEach((a) => { articlesById[a.id] = a; });

  const articles = (raw.articles || []).map((a) => ({
    id: a.id,
    article_name: a.article,
    location: a.location,
    type: a.type,
    assigned_staff: a.assigned_staff_details?.name || null,
    est_time: a.est_time,
    description: a.description,
    materials: (a.materials || []).map((m) => ({ id: m.id, material_name: m.material, qty: m.qty, unit: m.unit })),
    site_activities: (a.site_activities || []).map((sa) => ({
      id: sa.id,
      activity_type: sa.activity_type,
      start_time: sa.start,
      end_time: sa.finish,
      self_remark: sa.self_remark,
      team_leader_remarks: sa.team_leader_remark,
      supervisor_remarks: sa.supervisor_remark,
    })),
  }));

  const photo_rows = (raw.photo_rows || []).map((pr) => {
    const article = articlesById[pr.article_id];
    return {
      id: pr.id,
      description: article ? article.article : `Article #${pr.article_id}`,
      job_card_photo: toPhotoUrl(pr.job_card_photo),
      before_photo: toPhotoUrlList(pr.activity_before_photos)[0] || toPhotoUrlList(pr.gallery_before_photos)[0] || null,
      after_photos: [...toPhotoUrlList(pr.activity_after_photos), ...toPhotoUrlList(pr.gallery_after_photos)],
      self_remark: pr.self_remark,
      team_leader_remarks: pr.team_leader_remarks,
      supervisor_remarks: pr.supervisor_remarks,
    };
  });

  return {
    id: jobRaw.id,
    job_no: jobRaw.job_no,
    client_name: jobRaw.client_name,
    location: jobRaw.location,
    status: jobRaw.status,
    team_leader: raw.team_leader || null,
    activities: raw.activities || [],
    materials_used: raw.materials_used || [],
    materials_issued: (raw.materials_issued || []).map((m) => ({
      id: m.id, material_name: m.material_name, issued_qty: m.issued_qty, unit: m.unit,
      used_qty: m.used, return_qty: m.return_qty, remarks: m.remarks,
    })),
    articles,
    photo_rows,
  };
}

function JobDetailPanel({ jobId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [lightbox, setLightbox] = useState(null);
  const [job, setJob] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError("");
    setTab("overview");
    API.get(`/reports/jobs/${jobId}`)
      .then((res) => {
        if (res.data?.success) {
          setJob(normalizeReportJobDetail(res.data.data));
        } else {
          setError(res.data?.message || "Unable to load job details.");
        }
      })
      .catch((err) => {
        console.error("Job detail error:", err);
        setError("Unable to load job details.");
      })
      .finally(() => setLoading(false));
  }, [jobId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const totalMins = job?.activities?.reduce((s, a) => {
    if (!a.start_time || !a.end_time) return s;
    const d = (new Date(`1970-01-01T${a.end_time}`) - new Date(`1970-01-01T${a.start_time}`)) / 60000;
    return s + (d > 0 ? d : 0);
  }, 0) ?? 0;

  return (
    <>
      <div className="jdp-overlay" onClick={onClose} />
      <div className="jdp-panel">
        {loading ? (
          <div className="jdp-loading"><div className="rpt-spinner" /> Loading job details…</div>
        ) : error ? (
          <div className="jdp-loading">{error}</div>
        ) : !job ? (
          <div className="jdp-loading">Job not found.</div>
        ) : (
          <>
            <div className="jdp-head">
              <div className="jdp-head-items">
                <div><div className="jdp-hi-lbl">Job No.</div><div className="jdp-hi-val" style={{ color: "#5b4cf5" }}>{job.job_no || `JOB-${String(job.id).padStart(4, "0")}`}</div></div>
                <div className="jdp-divider" />
                <div><div className="jdp-hi-lbl">Client</div><div className="jdp-hi-val">{job.client_name}</div></div>
                <div className="jdp-divider" />
                <div><div className="jdp-hi-lbl">Location</div><div className="jdp-hi-val" style={{ fontWeight: 500 }}>{job.location}</div></div>
                <div className="jdp-divider" />
                <div><div className="jdp-hi-lbl">Team Leader</div><div className="jdp-hi-val" style={{ fontWeight: 500 }}>{job.team_leader?.name || "—"}</div></div>
                <div className="jdp-divider" />
                <div><div className="jdp-hi-lbl">Status</div><span className={`rpt-badge ${STATUS_BADGE[job.status] ?? "badge-gray"}`}>{capitalize(job.status)}</span></div>
              </div>
              <button className="jdp-close" onClick={onClose}>
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><line x1="13" y1="1" x2="1" y2="13" /><line x1="1" y1="1" x2="13" y2="13" /></svg>
              </button>
            </div>

            <div className="jdp-tabs">
              {[
                { key: "overview", label: "Overview" },
                { key: "articles", label: "Articles & Activities" },
                { key: "issued", label: "Materials Issued" },
                { key: "photos", label: "Photos" },
              ].map((t) => (
                <button key={t.key} className={`jdp-tab${tab === t.key ? " jdp-tab-on" : ""}`} onClick={() => setTab(t.key)}>{t.label}</button>
              ))}
            </div>

            <div className="jdp-body">
              {tab === "overview" && (
                <>
                  <div className="jdp-stat-row">
                    <div className="jdp-stat jdp-stat-blue"><div className="jdp-stat-lbl" style={{ color: "#3b82f6" }}>Total Duration</div><div className="jdp-stat-val">{fmtHM(totalMins)}</div></div>
                    <div className="jdp-stat jdp-stat-green"><div className="jdp-stat-lbl" style={{ color: "#10b981" }}>Activities Logged</div><div className="jdp-stat-val">{job.activities?.length ?? 0}</div></div>
                    <div className="jdp-stat jdp-stat-amber"><div className="jdp-stat-lbl" style={{ color: "#f59e0b" }}>Materials Used</div><div className="jdp-stat-val">{job.materials_used?.length ?? 0}</div></div>
                  </div>

                  <div className="jdp-card">
                    <div className="jdp-card-hd"><div className="t">Activity Timeline</div><div className="s">All logged activities for this job</div></div>
                    <table className="jdp-mini-table warn">
                      <thead><tr><th>Date</th><th>Staff</th><th>Activity</th><th>Start</th><th>End</th></tr></thead>
                      <tbody>
                        {(!job.activities || job.activities.length === 0) ? (
                          <tr><td colSpan={5} className="jdp-empty-row">No activities logged for this job.</td></tr>
                        ) : job.activities.map((a) => (
                          <tr key={a.id}>
                            <td>{fmt(a.date)}</td><td>{a.staff_name || "—"}</td>
                            <td><span className="rpt-badge badge-purple">{a.activity_type}</span></td>
                            <td>{fmtTime(a.start_time)}</td><td>{fmtTime(a.end_time)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="jdp-card">
                    <div className="jdp-card-hd"><div className="t">Materials Used</div><div className="s">Logged per staff member</div></div>
                    <table className="jdp-mini-table">
                      <thead><tr><th>Date</th><th>Staff</th><th>Material</th><th>Qty</th></tr></thead>
                      <tbody>
                        {(!job.materials_used || job.materials_used.length === 0) ? (
                          <tr><td colSpan={4} className="jdp-empty-row">No materials recorded.</td></tr>
                        ) : job.materials_used.map((m) => (
                          <tr key={m.id}><td>{fmt(m.date)}</td><td>{m.staff_name || "—"}</td><td>{m.material_name}</td><td>{m.quantity} {m.unit}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {tab === "articles" && (
                <>
                  {(!job.articles || job.articles.length === 0) ? (
                    <div className="jdp-card"><div className="jdp-empty-row">No articles recorded for this job.</div></div>
                  ) : (
                    job.articles.map((art, index) => {
                      const articleActivities = art.site_activities || [];
                      const materialSummary = art.materials?.length > 0
                        ? art.materials.map((m) => `${m.material_name || "—"} × ${m.qty ?? 0}${m.unit ? ` ${m.unit}` : ""}`).join(" · ")
                        : "—";

                      return (
                        <div className="jdp-aa-card" key={art.id}>
                          <div className="jdp-aa-head">
                            <div className="jdp-aa-head-left">
                              <div className="jdp-aa-number">{index + 1}</div>
                              <div>
                                <div className="jdp-aa-title">{art.article_name || "—"}</div>
                                <div className="jdp-aa-meta">
                                  {art.location || "—"}
                                  {art.type && (<><span className="jdp-aa-dot">•</span>{art.type}</>)}
                                  {art.assigned_staff && (<><span className="jdp-aa-dot">•</span>{art.assigned_staff}</>)}
                                  {art.est_time && (<><span className="jdp-aa-dot">•</span>{art.est_time}</>)}
                                </div>
                              </div>
                            </div>
                            <div className="jdp-aa-head-right">
                              <span className="jdp-aa-material-summary">{materialSummary}</span>
                            </div>
                          </div>

                          <div className="jdp-aa-section">
                            <div className="jdp-aa-section-title">Article Details</div>
                            <div className="jdp-aa-table-wrap">
                              <table className="jdp-aa-table">
                                <thead>
                                  <tr>
                                    <th>ARTICLE</th><th>LOCATION</th><th>JOB TYPE</th><th>JOB DESCRIPTION</th>
                                    <th>STAFF</th><th>EST. TIME</th><th>MATERIALS</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td className="jdp-aa-strong">{art.article_name || "—"}</td>
                                    <td>{art.location || "—"}</td>
                                    <td>{art.type ? <span className="jdp-aa-type">{art.type}</span> : "—"}</td>
                                    <td className="jdp-aa-description">{art.description || "—"}</td>
                                    <td>{art.assigned_staff || "—"}</td>
                                    <td>{art.est_time || "—"}</td>
                                    <td>
                                      {art.materials?.length > 0 ? (
                                        <div className="jdp-aa-materials">
                                          {art.materials.map((m) => (
                                            <span className="jdp-aa-material" key={m.id}>
                                              {m.material_name || "—"} × {m.qty ?? 0}{m.unit ? ` ${m.unit}` : ""}
                                            </span>
                                          ))}
                                        </div>
                                      ) : "—"}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="jdp-aa-section">
                            <div className="jdp-aa-activity-title">
                              <span>{art.article_name || `Article ${index + 1}`}</span>
                              <span className="jdp-aa-activity-title-light">Activity Log</span>
                            </div>
                            <div className="jdp-aa-table-wrap">
                              <table className="jdp-aa-table jdp-aa-activity-table">
                                <thead>
                                  <tr>
                                    <th>S.NO</th><th>SITE ACTIVITY</th><th>START</th><th>FINISH</th>
                                    <th>SELF REMARK</th><th>TEAM LEADER</th><th>SUPERVISOR</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {articleActivities.length === 0 ? (
                                    <tr><td colSpan={7} className="jdp-aa-empty">No activities recorded for this article.</td></tr>
                                  ) : (
                                    articleActivities.map((activity, activityIndex) => (
                                      <tr key={activity.id}>
                                        <td className="jdp-aa-sno">{activityIndex + 1}</td>
                                        <td className="jdp-aa-activity-name">{activity.activity_type || "—"}</td>
                                        <td>{fmtTime(activity.start_time)}</td>
                                        <td>{fmtTime(activity.end_time)}</td>
                                        <td>{activity.self_remark || "—"}</td>
                                        <td>{activity.team_leader_remarks || "—"}</td>
                                        <td>{activity.supervisor_remarks || "—"}</td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </>
              )}

              {tab === "issued" && (
                <div className="jdp-card">
                  <div className="jdp-card-hd"><div className="t">Materials Issued</div><div className="s">Supervisor-tracked issue / return</div></div>
                  <table className="jdp-mini-table warn">
                    <thead><tr><th>Material</th><th>Issued</th><th>Unit</th><th>Used</th><th>Return</th><th>Remarks</th></tr></thead>
                    <tbody>
                      {(!job.materials_issued || job.materials_issued.length === 0) ? (
                        <tr><td colSpan={6} className="jdp-empty-row">No materials issued for this job.</td></tr>
                      ) : job.materials_issued.map((m) => (
                        <tr key={m.id}>
                          <td style={{ fontWeight: 600 }}>{m.material_name}</td>
                          <td>{m.issued_qty}</td><td>{m.unit}</td><td>{m.used_qty ?? "—"}</td><td>{m.return_qty ?? "—"}</td><td>{m.remarks || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === "photos" && (
                <div className="jdp-card">
                  <div className="jdp-card-hd"><div className="t">Photos</div><div className="s">Click any thumbnail to zoom</div></div>
                  <table className="jdp-mini-table">
                    <thead><tr><th>Description</th><th>Job Card</th><th>Before</th><th>After</th><th>Self Remark</th><th>Team Leader</th><th>Supervisor</th></tr></thead>
                    <tbody>
                      {(!job.photo_rows || job.photo_rows.length === 0) ? (
                        <tr><td colSpan={7} className="jdp-empty-row">No photos uploaded for this job.</td></tr>
                      ) : job.photo_rows.map((r) => (
                        <tr key={r.id}>
                          <td>{r.description || "—"}</td>
                          <td>{r.job_card_photo ? <img className="jdp-thumb" src={r.job_card_photo} onClick={() => setLightbox(r.job_card_photo)} alt="" /> : <div className="jdp-thumb-empty" />}</td>
                          <td>{r.before_photo ? <img className="jdp-thumb" src={r.before_photo} onClick={() => setLightbox(r.before_photo)} alt="" /> : <div className="jdp-thumb-empty" />}</td>
                          <td>
                            <div className="jdp-photo-cell">
                              {r.after_photos?.length ? r.after_photos.map((p, i) => (
                                <img className="jdp-thumb" key={i} src={p} onClick={() => setLightbox(p)} alt="" />
                              )) : <div className="jdp-thumb-empty" />}
                            </div>
                          </td>
                          <td>{r.self_remark || "—"}</td>
                          <td>{r.team_leader_remarks || "—"}</td>
                          <td>{r.supervisor_remarks || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <img className="lightbox-img" src={lightbox} alt="" />
        </div>
      )}
    </>
  );
}